import { memo, useEffect, useState } from "react";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { TextArea } from "ui/components/TextField/TextArea";
import EmailTextField from "ui/components/TextField/EmailTextField/EmailTextField";
import { useTranslate } from "helpers";
import { useHandleTextFieldChange } from "services/forms/hooks";

import { getPaymentQrPreviewAPI, sendPaymentQrAPI } from "services/api/fleet/vehicles/offers/api";

import { PaymentQrModalProps, PaymentQrFormValues } from "./types";
import { ZPosition } from "ui/components/Modal/types";

const PaymentQrModal: React.FC<PaymentQrModalProps> = ({
  show,
  onHide,
  formValues,
  total,
}) => {
  const { t } = useTranslate();

  const getInitialValues = (): PaymentQrFormValues => ({
    amount: total > 0 ? total.toString() : "",
    email: "",
    subject: `${t("admin.fleet.vehicle.offer.rent.payment.qr.subject.prefix")} ${formValues.offerNumber ?? ""}`,
    content: t("admin.fleet.vehicle.offer.rent.payment.qr.content.default"),
  });

  const [qrFormValues, setQrFormValues] = useState<PaymentQrFormValues>(getInitialValues);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [qrPreviewUrl, setQrPreviewUrl] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  const handleTextFieldChange = useHandleTextFieldChange(setQrFormValues, qrFormValues);

  // Reset forme kad se modal otvori
  useEffect(() => {
    if (show) {
      setQrFormValues(getInitialValues());
      setError("");
      setIsSent(false);
      setQrPreviewUrl(null);
    }
  }, [show, total]);

  // Generiraj QR preview kad se modal otvori ili kad se promijeni iznos (s debounce-om)
  useEffect(() => {
    if (!show || !formValues.id) return;

    const numAmount = parseFloat(qrFormValues.amount);
    if (!qrFormValues.amount || isNaN(numAmount) || numAmount <= 0 || numAmount > total) {
      setQrPreviewUrl(null);
      return;
    }

    const timeout = setTimeout(() => {
      fetchQrPreview(numAmount);
    }, 500);

    return () => clearTimeout(timeout);
  }, [show, qrFormValues.amount, formValues.id]);

  const fetchQrPreview = async (amount: number) => {
    if (!formValues.id) return;
    setQrLoading(true);
    try {
      const url = await getPaymentQrPreviewAPI(formValues.id, amount);
      setQrPreviewUrl(url);
    } catch {
      setQrPreviewUrl(null);
    }
    setQrLoading(false);
  };

  const validate = (): boolean => {
    const numAmount = parseFloat(qrFormValues.amount);
    if (!qrFormValues.amount || isNaN(numAmount) || numAmount <= 0) {
      setError(t("admin.fleet.vehicle.offer.rent.payment.qr.error.amount.min"));
      return false;
    }
    if (numAmount > total) {
      setError(`${t("admin.fleet.vehicle.offer.rent.payment.qr.error.amount.max")} ${total.toLocaleString("hr-HR")} €`);
      return false;
    }
    if (!qrFormValues.email.trim()) {
      setError(t("admin.fleet.vehicle.offer.rent.payment.qr.error.email.required"));
      return false;
    }
    return true;
  };

  const handleSend = async () => {
    if (!validate()) return;
    if (!formValues.id) return;

    setIsLoading(true);
    try {
      await sendPaymentQrAPI(formValues.id, {
        amount: parseFloat(qrFormValues.amount),
        recipients: qrFormValues.email.split(";").map((e) => e.trim()).filter(Boolean),
        replyTo: "",
        subject: qrFormValues.subject,
        content: qrFormValues.content,
      });
      setIsSent(true);
    } catch {
      // error handled by snackbar
    }
    setIsLoading(false);
  };

  const handleClose = () => {
    if (qrPreviewUrl) URL.revokeObjectURL(qrPreviewUrl);
    setQrFormValues(getInitialValues());
    setError("");
    setIsSent(false);
    setQrPreviewUrl(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} position="centered" size="small" zPosition={ZPosition.Top}>
      <ModalHeader title={t("admin.fleet.vehicle.offer.rent.payment.qr.title")} onHide={handleClose} icon="/media/images/add-icon.svg" />
      <ModalBody>
        <div className="p-4">
          {isSent ? (
            <div className="bg-green-50 border border-green-200 rounded p-4 text-center">
              <p className="text-green-800 font-medium">
                {t("admin.fleet.vehicle.offer.rent.payment.qr.sent.success")}
              </p>
            </div>
          ) : (
            <>
              {/* QR Preview */}
              <div className="flex justify-center mb-4">
                <div className="w-48 h-48 border border-slate-200 rounded flex items-center justify-center bg-white">
                  {qrLoading ? (
                    <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin" />
                  ) : qrPreviewUrl ? (
                    <img src={qrPreviewUrl} alt="HUB3 QR" className="w-full h-full object-contain p-2" />
                  ) : (
                    <span className="text-sm text-slate-400 text-center px-4">
                      {t("admin.fleet.vehicle.offer.rent.payment.qr.preview.empty")}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-1 mb-3">
                  <TextField
                    type="number"
                    name="amount"
                    label={t("admin.fleet.vehicle.offer.rent.payment.qr.amount")}
                    placeholder={t("admin.fleet.vehicle.offer.rent.payment.qr.amount.placeholder")}
                    value={qrFormValues.amount}
                    onChange={handleTextFieldChange}
                    groupAppend="€"
                    error={error && error.includes(t("admin.fleet.vehicle.offer.rent.payment.qr.error.amount.keyword")) ? error : undefined}
                    required
                  />
                </div>
                <div className="flex-1 mb-3">
                  <EmailTextField
                    name="email"
                    label={t("admin.fleet.vehicle.offer.rent.payment.qr.email")}
                    value={qrFormValues.email}
                    onChange={handleTextFieldChange}
                    error={error && error.includes("Email") ? error : undefined}
                    required
                  />
                </div>
              </div>
              <div className="flex-1 mb-3">
                <TextField
                  type="text"
                  name="subject"
                  label={t("admin.fleet.vehicle.offer.rent.payment.qr.subject")}
                  value={qrFormValues.subject}
                  onChange={handleTextFieldChange}
                />
              </div>
              <div className="flex-1 mb-3">
                <TextArea
                  name="content"
                  label={t("admin.fleet.vehicle.offer.rent.payment.qr.content")}
                  value={qrFormValues.content}
                  onChange={handleTextFieldChange}
                  rows={6}
                />
              </div>
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
      <div className="px-3 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
          <Button variant="outlined" onClick={handleClose}>
            {isSent ? t("admin.close") : t("admin.cancel")}
          </Button>
          {!isSent && (
            <Button
              variant="primary"
              onClick={handleSend}
              disabled={isLoading}
            >
              {isLoading ? t("admin.fleet.vehicle.offer.rent.payment.qr.sending") : t("admin.fleet.vehicle.offer.rent.payment.qr.send")}
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(PaymentQrModal);
